// The thermal payload, as the console reads it.
//
// EVERY FIELD IS OPTIONAL, deliberately. This document is built by a separate
// Python project on its own release cycle; a console that destructures it
// confidently turns "the model gained a field" into a blank page for a client.
// Reading defensively and degrading per-panel is the contract.
//
// The payload is also the DISCLOSURE SURFACE: `server/thermal.go` serves the
// file verbatim, so anything in it reaches the browser whether or not a panel
// draws it. Feature names, fitted coefficients and the rejected-candidate list
// are SeaState IP and must not be in the document before it arrives. That is a
// build-side gate (Layer 4), not something this file can enforce — but reading
// only what is listed here is what keeps the two in step.

export interface Day {
  date?: string;
  measured_f?: number | null;
  measured_mean_f?: number | null;
  measured_is_mean?: boolean;
  /** The latest day, not yet whole: the max SO FAR, with how many hours it
   *  holds and the last hour it reached. Never both this and measured_f. */
  measured_partial_f?: number | null;
  measured_hours?: number;
  measured_to?: string;
  estimate_f?: number | null;
  forecast_f?: number | null;
  lo_f?: number | null;
  hi_f?: number | null;
  decision_f?: number | null;
  normal_f?: number | null;
  reduced?: boolean;
  /** The head-to-head over the console window (build_panel.py main()): our
   *  model fitted BEFORE the window and run on the day's observed inputs (past
   *  days only), and PG&E's incumbent -- Open-Meteo at the buoy plus a seasonal
   *  offset -- on the same inputs, past and forward. */
  hindcast_f?: number | null;
  pge_f?: number | null;
}

export interface Driver {
  date?: string;
  future?: boolean;
  sst_f?: number;
  air_f?: number;
  solar?: number;
  wind?: number;
  cloud?: number;
}

export interface LeadSkill {
  lead?: number;
  n?: number;
  rmse_f?: number;
  bias_f?: number;
  p90_f?: number;
}

export interface SweepRow {
  f?: number;
  days?: number;
  episodes?: number;
  watch_pod?: number;
  alarm_csi?: number;
  alarm_fa_yr?: number;
}

export interface DetectorRow {
  rule?: string;
  hits?: number;
  misses?: number;
  fa?: number;
  pod?: number;
  csi?: number;
  fa_yr?: number;
  note?: string;
}

/**
 * The shipped bound's detection skill at ONE forecast lead.
 *
 * DetectorRow above is scored at lead 0 — the prediction is indexed on the day
 * it predicts. That answers "can this spot a crossing today". PG&E buy a nine-day
 * forecast, and the answer there is very different: CSI 0.387 at lead 0 against
 * 0.066 at lead 9. Both numbers are correct; shipping only the first one meant
 * the console answered a question the client was not asking.
 */
export interface LeadDetectorRow {
  lead?: number;
  hits?: number;
  misses?: number;
  fa?: number;
  pod?: number;
  csi?: number;
  fa_yr?: number;
  events?: number;
  days?: number;
}

/**
 * Measured blind coverage of the decision bound at one lead.
 *
 * `tau` is what the quantile is FITTED at. This is what it delivers. They are
 * not the same number — 0.975 nominal against ~0.938 measured at lead 1 falling
 * to ~0.913 at lead 9 — and the console shows the measured one, because a
 * "97.5th percentile" that is breached 6% of the time is the product overstating
 * itself in the one direction a margin product must never overstate.
 */
export interface CoverageRow {
  lead?: number;
  n?: number;
  breached?: number;
  rate?: number;
  percentile?: number;
}

/**
 * One forward day from the ocean-model ensemble, every member corrected onto the
 * intake DAILY MAX with its own multi-year seasonal fit.
 *
 * THE SPREAD IS THE PRODUCT. Our forward driver was Open-Meteo alone — the same
 * single source PG&E's own offset model runs on — so beyond a day or two both
 * were reading one ocean model. HYCOM and CMEMS make it three, and the width
 * between them is the one thing a single-source model can never say: "this
 * week the models disagree by 4 °F, hold more margin." On 2026-10-07 the
 * corrected spread was 4.2 °F against ~1.3 °F residual per model.
 */
export interface ModelSpreadRow {
  date?: string;
  hycom?: number;
  cmems?: number;
  lo_f?: number;
  hi_f?: number;
  openmeteo?: number;
  spread_f?: number;
}

/** A station's own readings and the model that stands in for it in our
 *  inputs, on that station's own scale, as [date, °F] pairs over the console
 *  window. 46215 also carries HYCOM and CMEMS. */
export interface StationModels {
  measured?: [string, number][];
  openmeteo?: [string, number][];
  hycom?: [string, number][];
  cmems?: [string, number][];
  openmeteo_fit?: { n?: number; resid_sd_f?: number };
}

export interface SkillMaxRow {
  lead?: number; n?: number; events?: number; rmse_f?: number; bias_f?: number;
  hits?: number; misses?: number; false_alarms?: number; pod?: number | null; csi?: number | null;
}
export interface HeadToHeadRow {
  lead?: number; n?: number; events?: number; ours_rmse?: number; pge_rmse?: number;
  ours_wins_pct?: number | null;
}
export interface WarmDecileRow { lead?: number; ours?: number; best_bench?: number; gain_pct?: number | null; }
export interface SkillMax {
  generated_at?: string; protocol?: string; target?: string; limitation?: string;
  ours?: SkillMaxRow[]; head_to_head?: HeadToHeadRow[]; warm_decile?: WarmDecileRow[];
}

/** How the intake track's two lines were made, for the label. */
export interface HeadToHead {
  window_from?: string;
  ours?: { fitted_to?: string | null; days?: number };
  pge?: { method?: string; per?: string; fit?: { n?: number; resid_sd_f?: number } | null; days?: number };
  basis?: string;
}

/** MUR 1 km raster: origin + spacing + row-major values, null for land. */
export interface SstField {
  date?: string;
  lat0?: number;
  lon0?: number;
  dlat?: number;
  dlon?: number;
  rows?: number;
  cols?: number;
  unit?: string;
  source?: string;
  wet?: number;
  grid?: (number | null)[];
}

/** One measuring point we depend on, and how it tracks the plant's own meter. */
export interface SourcePoint {
  id?: string;
  name?: string;
  kind?: "intake" | "buoy" | "station";
  lat?: number;
  lon?: number;
  km?: number;
  role?: string;
  /** "daily minimum" / "daily maximum" — different quantities, always stated. */
  basis?: string;
  vs_intake?: { n?: number; r?: number; bias_f?: number; sd_f?: number } | null;
  /** [date, °F] pairs. */
  series?: [string, number][];
}

export interface HourlyTrace {
  unit?: string;
  first?: string;
  last?: string;
  /** How old the export is. Printed, never hidden — see the DEMO banner. */
  stale_days?: number;
  source?: string;
  points?: [string, number][];
}

export interface Payload {
  generated_at?: string;
  model?: { kind?: string; trained_days?: number; trained_from?: string; trained_to?: string; form?: string };
  drivers?: Driver[];
  verification?: { series?: unknown[]; by_lead?: LeadSkill[] };
  baselines?: { name?: string; by_lead?: Record<string, number> }[];
  site?: { name?: string; lat?: number; lon?: number; buoy?: string };
  thresholds?: { action_f?: number; ceiling_f?: number };
  last_reported?: string;
  today?: string;
  current?: { estimate_f?: number; estimate_c?: number; band_f?: number; margin_f?: number; normal_f?: number };
  forecast_peak?: { date?: string; value_f?: number; margin_f?: number } | null;
  // Was `p_exceed_14d`, which kept saying 14 after the horizon moved to 9. The
  // number was always computed over the real forward window; only the name was
  // stale. `horizon_days` is emitted alongside so nothing has to read a period
  // out of a key name again.
  horizon_days?: number;
  p_exceed_horizon?: number;
  /** Present only on a backdated build (`build_panel.py --as-of`). Anything that
   *  shows LIVE data beside this forecast has to say the two are different days
   *  — see conditions.ts. */
  as_of?: { date?: string; days_back?: number; note?: string } | null;
  decision?: {
    tier?: string;
    tau?: number;
    peak_f?: number | null;
    margin_f?: number | null;
    reduced_now?: boolean;
    reduced_in_window?: boolean;
    flow_known_to?: string | null;
    /** measured, not nominal — see CoverageRow */
    coverage_by_lead?: CoverageRow[];
    coverage_at_peak_pct?: number | null;
    peak_lead?: number | null;
    /** scored at lead 0 — see `table_lead_basis` */
    table?: DetectorRow[];
    table_by_lead?: LeadDetectorRow[];
    table_lead_basis?: number;
    sweep?: SweepRow[];
    scored_days?: number;
    scored_events?: number;
    scored_episodes?: number;
  };
  band_by_lead_c?: Record<string, number>;
  model_spread?: ModelSpreadRow[];
  model_spread_meta?: Record<string, { pulled_hours_ago?: number }>;
  station_models?: Record<string, StationModels>;
  head_to_head?: HeadToHead;
  /** The Skill tab: the product's own quantity scored blind, read from
   *  research/max_benchmark.json and beat_the_benchmark.json by the build. */
  skill_max?: SkillMax | null;
  series?: Day[];
  skill_basis?: {
    protocol?: string;
    external?: LeadSkill[];
    with_meter?: LeadSkill[];
    gain?: { lead?: number; external_f?: number; with_meter_f?: number; gain_pct?: number }[];
  };
  intake_hourly?: HourlyTrace | null;
  sst_field?: SstField | null;
  sources?: SourcePoint[];
}

/** A finite number, or null. The payload uses null for "no value" and the JSON
 *  can also carry undefined once a field is optional — both mean the same thing
 *  to a chart, and neither should reach one as NaN. */
export function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export function f1(v: number | null | undefined, unit = " °F"): string {
  const n = num(v);
  return n === null ? "—" : `${n.toFixed(1)}${unit}`;
}

/**
 * The DECISION BOUND's own formatter — two decimals, and not for tidiness.
 *
 * A bound of 63.97 against a 64 °F threshold is NORMAL, correctly: it has not
 * reached the line. Printed at one decimal it reads "64.0 °F" sitting beside the
 * word NORMAL, which is a screen arguing with itself at the one moment a reader
 * is deciding whether to act. The payload publishes this field at 2 dp for
 * exactly this reason ("the page decides precision" — build_panel.py), so the
 * page has to actually use it.
 */
export function fBound(v: number | null | undefined, unit = " °F"): string {
  const n = num(v);
  return n === null ? "—" : `${n.toFixed(2)}${unit}`;
}

/**
 * The sub-label under the decision bound: what it MEASURES, not what it is
 * fitted at.
 *
 * This used to be `${tau * 100}th percentile` read straight off the fitting
 * parameter, which asserted a coverage the bound does not have — blind
 * walk-forward puts it near the 94th at one day and the 91st at nine. On a
 * margin product, overstating a bound is the one direction that matters, so the
 * measured figure wins whenever it exists.
 *
 * When the scoring run has not been done the fallback names tau and says
 * "nominal" out loud, rather than silently reasserting the old claim. A missing
 * measurement should read as missing.
 */
/** The model-spread row for a date, or undefined. Pure, so the verdict, the
 *  grid and the chart all read the same row and cannot disagree. */
export function spreadAt(p: Payload, date: string | undefined): ModelSpreadRow | undefined {
  if (date === undefined) return undefined;
  return (p.model_spread ?? []).find((r) => r.date === date);
}

/** The ocean models that actually have a value on this row — the envelope's
 *  width is only meaningful for the members present. Three is the full set
 *  (HYCOM, CMEMS, Open-Meteo); the console prints the count rather than
 *  asserting "3-model" when HYCOM's run has ended two days early. */
export const ENSEMBLE_MEMBERS = ["hycom", "cmems", "openmeteo"] as const;
export function memberCount(row: ModelSpreadRow | undefined): number {
  if (row === undefined) return 0;
  return ENSEMBLE_MEMBERS.filter((k) => typeof row[k] === "number" && Number.isFinite(row[k])).length;
}

/** The head-to-head scorecard: over the window's measured days, how many did
 *  each method land within `tol` °F of the measured daily max, and each
 *  method's RMSE. Only days where BOTH methods have a value count, so the two
 *  numbers share one denominator. */
export function scorecard(series: readonly Day[], tol = 1.0): {
  n: number; ours: number; pge: number; ours_rmse: number | null; pge_rmse: number | null;
} {
  let n = 0;
  let ours = 0;
  let pge = 0;
  let so = 0;
  let sp = 0;
  for (const d of series) {
    const m = num(d.measured_f);
    const a = num(d.hindcast_f);
    const b = num(d.pge_f);
    if (m === null || a === null || b === null) continue;
    n++;
    if (Math.abs(a - m) <= tol) ours++;
    if (Math.abs(b - m) <= tol) pge++;
    so += (a - m) * (a - m);
    sp += (b - m) * (b - m);
  }
  return {
    n, ours, pge,
    ours_rmse: n > 0 ? Math.sqrt(so / n) : null,
    pge_rmse: n > 0 ? Math.sqrt(sp / n) : null,
  };
}

export function boundLabel(
  d: Payload["decision"], fallbackLead?: number,
): string {
  const pct = d?.coverage_at_peak_pct;
  if (pct === undefined || pct === null || !Number.isFinite(pct)) {
    return `${((d?.tau ?? 0.975) * 100).toFixed(1)}th pct (nominal)`;
  }
  const lead = d?.peak_lead ?? fallbackLead;
  const at = lead === undefined || lead === null ? "" : ` at ${lead} d`;
  return `${pct.toFixed(1)}th pct measured${at}`;
}

/** Band half-width at a lead, in °F, interpolated from `band_by_lead_c`.
 *
 *  The payload publishes the band in CELSIUS at leads 0/1/3/7/14 because that is
 *  the model's own unit; every reading here is Fahrenheit. Converting a
 *  half-width is a scale change (×9/5) with no +32 — the offset would turn a
 *  0.35 °C band into a 32.6 °F one, which is the kind of mistake that looks
 *  plausible on screen. */
export function bandForLead(p: Payload, lead: number): number | null {
  const t = p.band_by_lead_c;
  if (t === undefined) return null;
  const keys = Object.keys(t)
    .map(Number)
    .filter((k) => Number.isFinite(k))
    .sort((a, b) => a - b);
  if (keys.length === 0) return null;
  const first = keys[0] as number;
  const last = keys[keys.length - 1] as number;
  if (lead <= first) return (t[String(first)] as number) * 1.8;
  if (lead >= last) return (t[String(last)] as number) * 1.8;
  for (let i = 1; i < keys.length; i++) {
    const hi = keys[i] as number;
    const lo = keys[i - 1] as number;
    if (lead <= hi) {
      const a = t[String(lo)] as number;
      const b = t[String(hi)] as number;
      const w = (lead - lo) / (hi - lo);
      return (a + (b - a) * w) * 1.8;
    }
  }
  return null;
}

/** The forecast day at a given lead from today, or null when the payload is
 *  shorter than the control offers. Layer 2 exists so this does not return null
 *  for leads 4–7; if it starts to, the frame regressed. */
export function dayAtLead(p: Payload, lead: number): Day | null {
  const today = p.today;
  const series = p.series ?? [];
  if (today === undefined) return null;
  const t = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  const want = new Date(t + lead * 86400000).toISOString().slice(0, 10);
  return series.find((d) => d.date === want) ?? null;
}


/**
 * The decision tier.
 *
 * Computed from the decision BOUND, never the central estimate — the bound is
 * what the margin and the tier are DEFINED from, so deriving the tier from
 * anything else would make the two disagree on screen.
 *
 * Not because the estimate cannot detect. That was true of an earlier
 * configuration and the claim outlived it: re-scored 2026-09-01 over 4,762 days
 * and thirteen crossings, the point estimate catches 11 at CSI 0.733 while the
 * bound catches 12 at CSI 0.387 — the bound buys one extra catch for sixteen
 * more false alarms. Both numbers are real; the choice is a false-alarm budget,
 * not a claim that one of them is blind.
 *
 * Those are LEAD-0 figures, which is the other thing this comment used to get
 * wrong by omission. At nine days the bound catches 6 of 13 at CSI 0.066 with
 * 5.8 false alarms a year. See `table_by_lead`.
 *
 * ALARM is WATCH plus a generating unit offline: a unit outage sheds two of the
 * four circulating-water pumps, which is exactly a 50 % flow signal, and the
 * crossings in the record cluster there.
 *
 * Pure, and separate from the console, so it can be tested without a browser —
 * this is the rule the whole product turns on.
 */
export function tierFor(
  bound: number | null,
  threshold: number,
  reducedInWindow: boolean,
): "NORMAL" | "WATCH" | "ALARM" {
  if (bound === null || !Number.isFinite(bound)) return "NORMAL";
  if (bound < threshold) return "NORMAL";
  return reducedInWindow ? "ALARM" : "WATCH";
}
